import { type JanusCtx, type Row, type StatusRow, activeStatuses, ecId, inventoryId, isInventoryRow, handBack, insertRoll, itemField, record, upper, definitionNotHeld } from '../context.js';
import { carrierHasTrigger } from './status-trigger-gate.js';
import { runTriggerFamily, triggerFamilyPreDraw } from '../trigger-executor.js';
import { removeStatusFromRow, statusRows, statusItemId, statusRemovalBefore, STATUS_IDS } from '../../statuses/expiry.js';
import { resolveRemovalSupport } from '../../statuses/removal-support.js';
import { skipUnported } from '../../unported.js';
import { isTransportStatus, removeTransportLink } from '../../statuses/riding-removal.js';
import { readStatusRowStack } from '../../compile/status-stack-reader.js';
import { empty } from '../../php-compat/index.js';
import { removeMonsterBits } from '../../inventory/monster-bit-removal.js';
import { creatureName, heldInventoryRows, resolveGmZoneExit, resolveConnectionCascade } from '../../statuses/gm-boundary.js';
import { inventoryField } from '../../../shared/array-types.js';
import { resolveExactRemovalTails, resolveTemporaryChargeRemovals } from '../../statuses/exact-removal-tails.js';
import { deathStatusRows } from '../../death/cleanup.js';
import { deathHolders } from '../../death/departure.js';
import { removalSummons, departSummon } from '../../death/compose.js';
import { prepareFormStatusRemoval } from '../../statuses/shapeshift-inventory.js';

const LOSES_STATUS = 'triggers_when_caster_loses_status_effect';

// StatusEffect ids (StatusEffect.php).
const CONCENTRATING = STATUS_IDS.concentrating;
const ROTTING = '62E83DEB-4569-AE3A-F671-FF514C508FD2';
const HASTED = '091B2BEE-7F61-D7DB-0CF1-6D255F4B7663';
const SPOILING = 'F7A1C3B2-5D64-4E18-9A70-2C8B41D5E093';
/** check_other_status_effect_specials() / remove_linked_*_counterpart_status(). */
const SPECIAL_NAMES = /^(?:hasted|piloting|piloted|rotting|spoiling|grappled|grappling|restrained|resting)$/;
const PORTED_SPECIALS = new Set([HASTED, ROTTING, SPOILING, STATUS_IDS.grappled, STATUS_IDS.grappling, STATUS_IDS.restrained, STATUS_IDS.resting]);

export function storedStatusHasSpecialTeardown(ctx: JanusCtx, status: Row): boolean {
  const id = statusItemId(status);
  const definition = ctx.read.definition(id) ?? record(status.status_arr);
  if (!definition) definitionNotHeld('remove_status_effects:definition-not-held', id);
  return Number(status.status_aoe_source ?? 0) !== 0 || SPECIAL_NAMES.test(String(itemField(definition, 'item_name') ?? '').toLowerCase())
    || id === CONCENTRATING || id === STATUS_IDS.summon_tag || String(itemField(definition, 'item_type')) === 'creature';
}

/** GM boundaries and ordinary expiry use the same supported special-removal set. */
export function storedStatusHasUnportedTeardown(ctx: JanusCtx, status: Row, mode: 'preserve' | 'single' | 'form' = 'single'): boolean {
  const id = statusItemId(status);
  const form = (mode === 'form' || ctx.saveBoundaryInventory || ctx.shapeshiftInventory !== undefined)
    && String(itemField(ctx.read.definition(id) ?? record(status.status_arr), 'item_type')) === 'creature';
  return storedStatusHasSpecialTeardown(ctx, status)
    && !(Number(ctx.read.value(status, 'status_aoe_source') ?? 0) === 0 && (form || PORTED_SPECIALS.has(id)));
}

/** Transport removal dismounts the pair; other rows preserve peers except a lone Concentrating survivor. */
export function removeStoredStatus(ctx: JanusCtx, target: Row, status: Row, mode: 'preserve' | 'single' | 'form' | 'healing' = 'preserve',
  options: { skipConnectedPeerCull?: boolean; itemSweep?: boolean; suppressCorpseDecay?: boolean; allTargetPeers?: boolean;
    deferSupport?: boolean;
    beforeLoss?: (ctx: JanusCtx, owner: Row) => void;
    onLoss?: (ctx: JanusCtx, owner: Row, itemId: string) => void } = {}): void {
  if (isTransportStatus(status)) { removeTransportLink(ctx, target, status); return; }
  if (isInventoryRow(target)) {
    const targetId = inventoryId(target);
    if (status.tick_added || ctx.statusesAdded.includes(status as unknown as StatusRow)) {
      const pending = ctx.statusesAdded.find(row => upper(row.target) === targetId && upper(row.status_item_id) === statusItemId(status)
        && upper(row.status_connected_id) === upper(ctx.read.value(status, 'status_connected_id')));
      if (pending) dropPendingRow(ctx, pending);
      removeStatusFromRow(target, upper(ctx.read.value(status, 'status_id')));
      return;
    }
    if (Number(ctx.read.value(status, 'status_aoe_source') ?? 0) === 1) resolveConnectionCascade(ctx, status, targetId);
    else resolveGmZoneExit(ctx, { statusId: upper(ctx.read.value(status, 'status_id')), itemId: statusItemId(status), targetId }, { abilityFrame: true });
    return;
  }
  // Healing suppresses Rotting's decay dispatch; resurrection separately suppresses its broad part sweep.
  const healedRotting = mode === 'healing' && statusItemId(status) === ROTTING && Number(status.status_aoe_source ?? 0) === 0;
  if (mode === 'healing') mode = 'single';
  // A zone enumerates its exact rows. Keep the shared boundary loss/support tails, and never cull a same-connection survivor.
  if (options.skipConnectedPeerCull) {
    const id = statusItemId(status), targetId = ecId(target), statusId = upper(status.status_id);
    const linked = id === STATUS_IDS.grappled || id === STATUS_IDS.grappling;
    resolveGmZoneExit(ctx, { statusId, itemId: id, targetId }, { support: false, linkTeardown: linked || id === STATUS_IDS.restrained, abilityFrame: true });
    if (linked) removeLinkedGrapple(ctx, target, status, true);
    if (!options.deferSupport) resolveRemovalSupport(ctx, targetId, status);
    return;
  }
  // Zone exits own preparation above; explicit Revert already prepared its cleanup.
  if (mode !== 'form') prepareFormStatusRemoval(ctx, target, status);
  if (status.tick_added || ctx.statusesAdded.includes(status as unknown as StatusRow)) {
    const pending = ctx.statusesAdded.find(s => upper(s.target) === ecId(target) && upper(s.status_item_id) === statusItemId(status)
      && upper(s.status_connected_id) === upper(status.status_connected_id));
    if (!pending) return;
    const event = { trigger: LOSES_STATUS, statusItemId: statusItemId(status) }, lossArm = hasLossTrigger(ctx, target, statusItemId(status));
    if (lossArm) triggerFamilyPreDraw(ctx, target, event);
    removePendingRow(ctx, target, pending, () => {
      if (options.onLoss) options.onLoss(ctx, target, statusItemId(status));
      else if (lossArm) runTriggerFamily(ctx, event, target, null, [target], false, undefined, true);
    }, options.allTargetPeers);
    if (!options.deferSupport) resolveRemovalSupport(ctx, ecId(target), status);
    if (statusItemId(status) === ROTTING && !options.suppressCorpseDecay) removeMonsterBits(ctx, target);
    return;
  }
  const { id, connection, statusId, targetId, event, lossArm } = planStoredStatusRemoval(ctx, target, status, mode, options);
  const historical = deathStatusRows(deathHolders([ctx.encounter, ...ctx.members.values()]));
  for (const member of [ctx.encounter, ...ctx.members.values()]) removeStatusFromRow(member, statusId);
  ctx.statusesRemoved.push({ status_id: statusId, status_item_id: id, target: targetId });
  options.beforeLoss?.(ctx, target);
  if (options.onLoss) options.onLoss(ctx, target, id);
  else if (lossArm) runTriggerFamily(ctx, event, target, null, [target], false, undefined, true);
  resolveStatusRemovalSpecials(ctx, target, status, healedRotting);
  for (const summon of removalSummons(ctx, status, historical)) departSummon(ctx, summon, historical);
  // Loss hooks may change the connection. Native peer lookup happens after those hooks and special teardown.
  const { peers, lone } = storedConnectionRemovalPlan(ctx, target, status, mode, false, options.allTargetPeers);
  for (const peer of peers) {
    if (ctx.statusesRemoved.some(s => upper(s.status_id) === upper(peer.status.status_id))) continue;
    const peerId = upper(peer.status.status_id);
    for (const member of [ctx.encounter, ...ctx.members.values()]) removeStatusFromRow(member, peerId);
    ctx.statusesRemoved.push({ status_id: peerId, status_item_id: statusItemId(peer.status), target: targetId });
  }
  endLoneConcentration(ctx, lone);
  if (mode === 'preserve') logNonMemberPeers(ctx, connection,
    PORTED_SPECIALS.has(id) ? removalSurvivors(ctx, connection, statusId) : connectionSurvivors(ctx, connection, statusId), PORTED_SPECIALS.has(id));
  if (!options.deferSupport) resolveRemovalSupport(ctx, targetId, status);
  if (healedRotting && !options.suppressCorpseDecay) removeMonsterBits(ctx, target);
}

/** State-only exact-row/connection proof, shared with damage wake and contest retries before their dice. */
export function planStoredStatusRemoval(ctx: JanusCtx, target: Row, status: Row, mode: 'preserve' | 'single' | 'form' = 'single',
  options: { itemSweep?: boolean; allTargetPeers?: boolean } = {}) {
  const id = statusItemId(status), connection = upper(status.status_connected_id);
  if (storedStatusHasUnportedTeardown(ctx, status, mode))
    handBack('remove_status_effects:stored-special-status');
  if (mode === 'preserve' && !options.itemSweep && statusRows(target, 'target').filter(s => statusItemId(s) === id).length !== 1)
    handBack('remove_status_effects:ambiguous-item');
  const statusId = upper(status.status_id), targetId = ecId(target);
  if (!/^[0-9A-F-]{36}$/.test(statusId) || (status.status_target_id && upper(status.status_target_id) !== targetId)
      || !ctx.members.has(targetId) || !statusRows(target, 'target').some(s => upper(s.status_id) === statusId
        && statusItemId(s) === id && upper(s.status_connected_id) === connection)) handBack('remove_status_effects:stored-row-not-held');
  const { peers, lone } = storedConnectionRemovalPlan(ctx, target, status, mode, true, options.allTargetPeers);
  const event = { trigger: LOSES_STATUS, statusItemId: id };
  const lossArm = hasLossTrigger(ctx, target, id);
  if (lossArm) triggerFamilyPreDraw(ctx, target, event);
  return { id, connection, statusId, targetId, peers, lone, event, lossArm };
}

function storedConnectionRemovalPlan(ctx: JanusCtx, target: Row, status: Row, mode: 'preserve' | 'single' | 'form', primaryPending = true, allTargetPeers = false) {
  const id = statusItemId(status), connection = upper(status.status_connected_id), statusId = upper(status.status_id), targetId = ecId(target);
  const form = mode === 'form' && String(itemField(ctx.read.definition(id) ?? record(status.status_arr), 'item_type')) === 'creature';
  const survivors = connectionSurvivors(ctx, connection, statusId);
  const peers = allTargetPeers ? survivors.filter(s => s.holder === targetId)
    : mode === 'preserve' ? [] : survivors.filter(s => s.holder === targetId && (form || connectedPeerEligible(ctx, status, s.status)));
  if (mode === 'single' && !allTargetPeers) peers.splice(1);
  if (peers.some(p => p.status.tick_added || !/^[0-9A-F-]{36}$/.test(upper(p.status.status_id))))
    handBack('remove_status_effects:connection-peer-not-held');
  const remaining = survivors.filter(s => !peers.includes(s));
  if (!allTargetPeers && mode === 'single' && peers.length && remaining.length === 1 && statusItemId(remaining[0]!.status) === CONCENTRATING
      && !ctx.members.has(remaining[0]!.holder)) handBack('remove_status_effects:concentration-holder-not-held');
  const lone = !allTargetPeers && (mode === 'preserve' || mode === 'single' && peers.length) ? loneConcentrationPlan(ctx, remaining) : null;
  if (ctx.statusesRemoved.length + peers.length + Number(primaryPending) + (lone ? 1 : 0) > 128) handBack('remove_status_effects:write-limit');
  return { peers, lone };
}

type ConnectionSurvivor = { member: Row | null; holder: string; key: string; status: Row };

function pendingRowIdentity(status: Row): string {
  const id = upper(status.status_id);
  return status.tick_added && id.startsWith('TICK-PENDING-') ? '' : id;
}

/** A peer cull is a raw delete: no second loss trigger or special tail. */
function dropPendingRow(ctx: JanusCtx, row: StatusRow): void {
  const index = ctx.statusesAdded.indexOf(row);
  if (index < 0) return;
  ctx.statusesAdded.splice(index, 1);
  for (const member of [ctx.encounter, ...ctx.members.values()]) for (const projection of ['target', 'caster'] as const) {
    const bucket = record(member.statuses);
    if (bucket) bucket[projection] = statusRows(member, projection).filter(s => !(s.tick_added
      && (row.status_id && pendingRowIdentity(s) ? pendingRowIdentity(s) === upper(row.status_id)
        : statusItemId(s) === upper(row.status_item_id)
          && upper(statusRemovalBefore(s, ecId(member)).status_target_id) === upper(row.target)
          && upper(s.status_connected_id) === upper(row.status_connected_id))));
  }
}

function pendingSurvivor(ctx: JanusCtx, peer: Pick<ConnectionSurvivor, 'holder' | 'status'>): StatusRow | undefined {
  const id = pendingRowIdentity(peer.status);
  return ctx.statusesAdded.find(s => upper(s.target) === peer.holder && upper(s.status_item_id) === statusItemId(peer.status)
    && upper(s.status_connected_id) === upper(peer.status.status_connected_id)
    && (!id || !s.status_id || upper(s.status_id) === id));
}

/** Cancel a queued add and its tick projections without deleting a stored row or its connection peers. */
export function cancelPendingStatus(ctx: JanusCtx, target: Row, status: Row): boolean {
  const id = pendingRowIdentity(status);
  if (id && !ctx.statusesAdded.some(s => s === status as unknown as StatusRow || upper(s.status_id) === id)) return false;
  const pending = pendingSurvivor(ctx, { holder: ecId(target), status });
  if (!pending) return false;
  dropPendingRow(ctx, pending);
  return true;
}

/** START projections have synthetic ids; they and the pending verdict row describe one live status. */
function removalSurvivors(ctx: JanusCtx, connection: string, removedId: string): ConnectionSurvivor[] {
  const pending = new Set<StatusRow>();
  return connectionSurvivors(ctx, connection, removedId).filter(peer => {
    const row = pendingSurvivor(ctx, peer);
    if (!row) return true;
    if (pending.has(row)) return false;
    pending.add(row);
    return true;
  });
}

/** Ordinary teardown clears every inverse/restraint on the connection; exact zone exits clear only their pair. */
function removeLinkedGrapple(ctx: JanusCtx, target: Row, status: Row, zone = false): void {
  const id = statusItemId(status);
  if (id !== STATUS_IDS.grappled && id !== STATUS_IDS.grappling) return;
  const targetId = ecId(target), connection = upper(ctx.read.value(status, 'status_connected_id'));
  const counterpart = id === STATUS_IDS.grappled ? STATUS_IDS.grappling : STATUS_IDS.grappled;
  const survivors = removalSurvivors(ctx, connection, upper(status.status_id) || 'pending');
  let partnerId: string | undefined;
  if (zone) {
    const before = statusRemovalBefore(status, targetId);
    partnerId = [upper(before.status_target_id), upper(ctx.read.value(status, 'caster') ?? before.status_caster_id)]
      .find(id => id && id !== targetId)
      ?? survivors.find(peer => statusItemId(peer.status) === counterpart && peer.holder !== targetId)?.holder;
  }
  const victimId = id === STATUS_IDS.grappled ? targetId : partnerId;
  const peers = survivors.filter(peer => {
    const peerItem = statusItemId(peer.status);
    return (peerItem === counterpart && (!zone || peer.holder === partnerId))
      || (peerItem === STATUS_IDS.restrained && (!zone || peer.holder === victimId));
  });
  // The legacy ordinary teardown deletes the inverse first; exact zone exits retain their held-row order.
  if (!zone) peers.sort((a, b) => Number(statusItemId(a.status) === STATUS_IDS.restrained) - Number(statusItemId(b.status) === STATUS_IDS.restrained));
  logNonMemberPeers(ctx, connection, peers, true);
  const heldPeers = peers.filter(peer => ctx.members.has(peer.holder));
  if (ctx.statusesRemoved.length + heldPeers.filter(peer => !pendingSurvivor(ctx, peer)).length > 128)
    handBack('remove_status_effects:write-limit');
  for (const peer of heldPeers) {
    const pending = pendingSurvivor(ctx, peer);
    if (pending) { dropPendingRow(ctx, pending); continue; }
    for (const member of [ctx.encounter, ...ctx.members.values()]) removeStatusFromRow(member, upper(peer.status.status_id));
    ctx.statusesRemoved.push({ status_id: upper(peer.status.status_id), status_item_id: statusItemId(peer.status), target: peer.holder,
      before: statusRemovalBefore(peer.status, peer.holder) });
  }
}

export function resolveStatusRemovalSpecials(ctx: JanusCtx, target: Row, status: Row, healedRotting = false): void {
  const id = statusItemId(status);
  removeLinkedGrapple(ctx, target, status);
  if (id === HASTED) resolveExactRemovalTails(ctx, target, status);
  else {
    const definition = ctx.read.definition(id) ?? record(status.status_arr);
    if (!definition) definitionNotHeld('remove_status_effects:definition-not-held', id);
    resolveTemporaryChargeRemovals(ctx, target, definition, status);
  }
  const rotting = id === ROTTING && !healedRotting;
  if (rotting) removeMonsterBits(ctx, target);
  const read = ctx.read.inventoryField ?? inventoryField;
  const corpseDue = rotting && [...heldInventoryRows(ctx).values()].some(body =>
    upper(read(body, 'inventory_encounter_creature_id')) === ecId(target)
      && Number(read(body, 'inventory_deleted') ?? 0) === 0 && Number(read(body, 'item_qty') ?? 1) > 0);
  if (corpseDue || id === SPOILING) {
    const duty = corpseDue ? 'corpse-bones-and-spill' : 'duration-inventory-destruction';
    const name = String(itemField(record(target.player_characters), 'player_name') ?? creatureName(ctx, ecId(target)));
    ctx.rows.push(skipUnported(`${name}:status-removal:${duty}:not-ported`));
  }
}

function connectedPeerEligible(ctx: JanusCtx, removed: Row, peer: Row, form = false): boolean {
  const id = statusItemId(removed), peerId = statusItemId(peer);
  if (id === STATUS_IDS.restrained && (peerId === STATUS_IDS.grappled || peerId === STATUS_IDS.grappling)) return false;
  if (peerId === STATUS_IDS.resting && id !== STATUS_IDS.resting) return false;
  const definition = ctx.read.definition(peerId) ?? record(peer.status_arr);
  if (!definition) definitionNotHeld('remove_status_effects:peer-definition-not-held', peerId);
  return form || String(itemField(definition, 'item_type')) !== 'creature';
}

/** Retire the exact source and every held target/caster projection once. */
export function removeLoneConcentration(ctx: JanusCtx, target: string, statusId: string): void {
  if (ctx.statusesRemoved.some(s => upper(s.status_id) === statusId)) return;
  ctx.statusesRemoved.push({ status_item_id: CONCENTRATING, target, status_id: statusId });
  for (const member of [ctx.encounter, ...ctx.members.values()]) removeStatusFromRow(member, statusId);
}

function endLoneConcentration(ctx: JanusCtx, lone: ReturnType<typeof loneConcentrationPlan>): void {
  if (!lone) return;
  removeLoneConcentration(ctx, ecId(lone.member), upper(lone.status.status_id));
  if (lone.lossArm) runTriggerFamily(ctx, { trigger: LOSES_STATUS, statusItemId: CONCENTRATING }, lone.member, null, [lone.member], false, undefined, true);
}

function loneConcentrationPlan(ctx: JanusCtx, survivors: ConnectionSurvivor[]): { member: Row; status: Row; lossArm: boolean } | null {
  if (survivors.length !== 1 || statusItemId(survivors[0]!.status) !== CONCENTRATING) return null;
  const lone = survivors[0]!;
  if (!lone.member || !ctx.members.has(lone.holder)) return null;
  if (!/^[0-9A-F-]{36}$/.test(upper(lone.status.status_id)) || lone.status.tick_added
      || ctx.statusesAdded.includes(lone.status as unknown as StatusRow)) handBack('remove_status_effects:stored-concentration-shape');
  const lossArm = hasLossTrigger(ctx, lone.member, CONCENTRATING);
  if (lossArm) triggerFamilyPreDraw(ctx, lone.member, { trigger: LOSES_STATUS, statusItemId: CONCENTRATING });
  return { member: lone.member, status: lone.status, lossArm };
}

/** Count every known row once, including zones and peers identified only by caster projections. */
export function connectionSurvivors(ctx: JanusCtx, connection: string, removedId: string): ConnectionSurvivor[] {
  if (!connection) return [];
  const encounterId = upper(ctx.read.value(ctx.encounter, 'encounter_id'));
  const seen = new Set<string>(), survivors: ConnectionSurvivor[] = [];
  const add = (status: Row, fallbackHolder = ''): void => {
    const holder = upper(status.status_target_id ?? status.target ?? fallbackHolder);
    if (!holder || upper(status.status_connected_id) !== connection || upper(status.status_id) === removedId
        || ctx.statusesRemoved.some(r => upper(r.target) === holder
          && (r.status_id ? upper(r.status_id) === upper(status.status_id) : upper(r.status_item_id) === statusItemId(status)))) return;
    const key = upper(status.status_id) || `${holder}:${statusItemId(status)}:${connection}`;
    if (seen.has(key)) return;
    seen.add(key);
    survivors.push({ member: holder === encounterId ? ctx.encounter : ctx.members.get(holder) ?? null, holder, key, status });
  };
  for (const member of ctx.members.values()) for (const status of activeStatuses(ctx, member)) add(status, ecId(member));
  for (const status of ctx.read.statuses(ctx.encounter)) add(status, encounterId);
  for (const status of ctx.statusesAdded) if (upper(status.target) === encounterId) add(status as unknown as Row, encounterId);
  for (const member of [ctx.encounter, ...ctx.members.values()]) for (const status of statusRows(member, 'caster')) {
    add(status);
  }
  return survivors;
}

/** A held caster projection can identify an off-roster peer, but cannot supply its holder's state or authorize a silent cascade. */
function logNonMemberPeers(ctx: JanusCtx, connection: string, survivors: ConnectionSurvivor[], named = false): void {
  for (const { member, holder, key } of survivors) {
    if (member) continue;
    const reason = named ? `${creatureName(ctx, holder)}:status-removal:connected-peer-not-member`
      : `remove_status_effects:stored-connection-peer-not-member:${holder}:${key}`;
    if (!ctx.rows.some(row => row.roll_type_1 === 'janus_unported_skip' && row.rolls_full === reason))
      ctx.rows.push(skipUnported(reason, { connection, target: holder, statusId: key }));
  }
}

function statusName(ctx: JanusCtx, id: string): string {
  const definition = ctx.read.definition(id);
  if (!definition) definitionNotHeld('remove_status_effects:definition-not-held', id);
  return String(itemField(definition, 'item_name') ?? '').toLowerCase();
}

/** fire_target_loses_status_effect_triggers(): any carrier on the loser with triggers_when_caster_loses_status_effect. */
function hasLossTrigger(ctx: JanusCtx, target: Row, id: string | Iterable<string>): boolean {
  return carrierHasTrigger(ctx, target, LOSES_STATUS, 'remove_status_effects:loss-trigger', id);
}

/** remove_status_effect_item_id_single_connection() on a row THIS use added (still pending in the verdict): the row goes,
 * then check_status_effect_and_delete_single_connected_id(): when a same-target row is still live on its connection, ONE such
 * peer goes (PHP breaks after the first) and a lone Concentrating left on the connection auto-ends; with none, it returns
 * first. A raw peer cull fires no second loss trigger or special tail. */
function removePendingRow(ctx: JanusCtx, target: Row, row: StatusRow, afterRowRemoved: () => void = () => {}, allTargetPeers = false): void {
  const removedId = upper(row.status_item_id);
  if (removedId === STATUS_IDS.piloting || removedId === STATUS_IDS.piloted) { removeTransportLink(ctx, target, row as unknown as Row); return; }
  if (SPECIAL_NAMES.test(statusName(ctx, removedId)) && !PORTED_SPECIALS.has(removedId))
    handBack('remove_status_effects:pending-special-status');
  dropPendingRow(ctx, row);
  // status_removal_api.php ~L171: the loser's triggers_when_caster_loses_status_effect fires HERE — after the row goes, before the
  // specials and the connected-peer sweep (a swept peer fires none).
  afterRowRemoved();
  resolveStatusRemovalSpecials(ctx, target, row as unknown as Row);
  const targetId = ecId(target);
  const connection = upper(row.status_connected_id);
  const removedIsCreature = String(itemField(ctx.read.definition(removedId), 'item_type') ?? '') === 'creature';
  const candidates = removalSurvivors(ctx, connection, upper(row.status_id) || 'pending').filter(peer => peer.holder === targetId);
  const firstPeer = candidates.find(other => connectedPeerEligible(ctx, row as unknown as Row, other.status, removedIsCreature));
  // status_removal_api.php:925 — no live same-target row left on the connection → return BEFORE the lone-survivor sweep
  // (a Concentrating source on ANOTHER creature survives the cleanse of its last rider here).
  if (!candidates.length) return;
  for (const peer of allTargetPeers ? candidates : firstPeer ? [firstPeer] : []) {
    const pending = pendingSurvivor(ctx, peer);
    if (pending) dropPendingRow(ctx, pending);
    else {
      if (ctx.statusesRemoved.length >= 128) handBack('remove_status_effects:write-limit');
      for (const member of [ctx.encounter, ...ctx.members.values()]) removeStatusFromRow(member, upper(peer.status.status_id));
      ctx.statusesRemoved.push({ status_id: upper(peer.status.status_id), status_item_id: statusItemId(peer.status), target: peer.holder });
    }
  }
  if (allTargetPeers) return;
  const survivors = removalSurvivors(ctx, connection, upper(row.status_id) || 'pending');
  const pendingSource = survivors.length === 1 && statusItemId(survivors[0]!.status) === CONCENTRATING
    ? pendingSurvivor(ctx, survivors[0]!) : undefined;
  if (pendingSource) dropPendingRow(ctx, pendingSource);
  else {
    const lone = loneConcentrationPlan(ctx, survivors);
    if (lone && ctx.statusesRemoved.length >= 128) handBack('remove_status_effects:write-limit');
    endLoneConcentration(ctx, lone);
  }
  logNonMemberPeers(ctx, connection, survivors, true);
}

/** includes/functions/janus/statuses/status_effect_removal_helpers.php:remove_status_effects */
export function removeStatusEffects(ctx: JanusCtx, ability: Row, layerName: string, target: Row): void {
  const entries = ctx.read.entries(ability, layerName);
  if (!entries.length) {
    if (ctx.read.has(ability, layerName)) handBack('remove_status_effects:status-id-not-held');
    return;
  }
  const ids = entries.map((entry) => upper(typeof entry === 'string' ? entry.split('|')[0] : itemField(record(entry), 'item_id')));
  if (ids.some((id) => !/^[0-9A-F-]{36}$/.test(id))) handBack('remove_status_effects:status-id-not-held');
  const existing = ctx.read.statuses(target);
  for (const status of existing) {
    const id = upper(status.status_item_id ?? itemField(record(status.status_arr), 'item_id'));
    if (ids.includes(id)) {
      // Restful Body promises one additional stack after the rest's own decrement.
      if (ctx.trigger === 'triggers_after_long_rest' && upper(itemField(ability, 'item_id')) === '2BA1A151-6019-3B67-5BFA-714B91129A59'
          && layerName === 'status_effect_removed_from_target_after_cast' && id === 'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D'
          && readStatusRowStack(status) > 1) {
        if (!ctx.statusStacks) handBack('restful-body:stack-save-unavailable');
        // The replacement save cannot carry these columns. Preserve the row rather than clearing its saved metadata.
        if (['status_private', 'status_shapeshift_monster_template_id', 'status_aoe_geometry',
          ...['year', 'month', 'day', 'hour', 'minute', 'second'].map(unit => 'status_paused_campaign_' + unit)]
          .some(key => !empty(ctx.read.value(status, key)))) handBack('restful-body:stack-metadata-save-unavailable');
        const before = readStatusRowStack(status);
        ctx.statusStacks!.push({ status_id: upper(status.status_id), status_item_id: id, target: ecId(target), before, status_stack: before - 1 });
        for (const held of statusRows(target, 'target')) if (upper(held.status_id) === upper(status.status_id)) held.status_stack = before - 1;
        insertRoll(ctx, { roll_type_1: 'status_stack_decreased', roll_item_id: id, by: target, to: target, roll_result: -1 });
        continue;
      }
      // A projected pending row belongs to statusesAdded, and is handled by removePendingRow below.
      if (!status.tick_added && !ctx.statusesRemoved.some(s => upper(s.status_id) === upper(status.status_id)))
        removeStoredStatus(ctx, target, status, 'preserve', { itemSweep: true });
    }
  }
  // Rows THIS use added to the target (e.g. Violent Focus curing the Frightened the same hit's failed contest applied).
  const targetId = ecId(target);
  const pending = ctx.statusesAdded.filter((row) => row.target === targetId && ids.includes(upper(row.status_item_id)));
  if (!pending.length) return;
  // The loser's triggers_when_caster_loses_status_effect (fire_target_loses_status_effect_triggers: no inciting target) — decided by
  // the RX twin and executed through janus() in trigger mode (lane RX 2026-09-28). A loser outside the member map → PHP.
  const loser = ctx.members.get(targetId);
  const lossArm = hasLossTrigger(ctx, target, ids);
  if (lossArm && !loser) handBack('remove_status_effects:loses-status-trigger');
  for (const row of pending) {
    if (!ctx.statusesAdded.includes(row)) continue;
    const event = { trigger: LOSES_STATUS, statusItemId: upper(row.status_item_id) };
    if (lossArm) triggerFamilyPreDraw(ctx, loser!, event);
    removePendingRow(ctx, target, row, () => { if (lossArm) runTriggerFamily(ctx, event, loser!, null, [loser!], false, undefined, true); });
    resolveRemovalSupport(ctx, targetId, row as unknown as Row);
  }
}
